import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Col, Collapse, Input, Row, Tabs } from 'antd';
import debounce from 'lodash/debounce.js';
import {
  useActiveTypesQuery,
  useSearchMutation,
} from '../../../../redux/services/search.jsx';
import { useTranslation } from 'react-i18next';
import SearchResultWrapper from './SearchResults/SearchResultWrapper.jsx';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { routes } from '../../../../utils/helpers/ApiClient.jsx';
import { getSearchTypeName } from '../../../../utils/helpers/CommonHelper.jsx';
import SearchResultFilter from './SearchResults/SearchResultFilter.jsx';
import { useDispatch, useSelector } from 'react-redux';
import { saveFilter } from '../../../../redux/slices/searchFiltersSlice.jsx';

const { Search } = Input;

const SearchPage = () => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();
  // RTK Query hook
  const [search, { isLoading }] = useSearchMutation();
  const { data: activeTypes, isLoading: isActiveTypesIsLoading } =
    useActiveTypesQuery();
  const [combinedData, setCombinedData] = useState({ hits: [] });
  const [usedActiveTypes, setUsedActivedTypes] = useState([]);
  const [searchParams] = useSearchParams();
  const [selectedTab, setSelectedTab] = useState(
    searchParams.get('tab') ? searchParams.get('tab') : 'all',
  );
  const filters = useSelector((state) => state.searchFilter);
  const dispach = useDispatch();

  const inputRef = useRef(null);

  // Focus the input when the modal opens
  useEffect(() => {
    if (inputRef.current) {
      setTimeout(() => {
        inputRef.current.focus();
      }, 200); // to give time open and render the input
    }
  }, []);

  const debouncedSearch = useCallback(
    debounce((query, searchAfter, dataTypes, filter) => {
      if (!query) return;
      const [field, order] = filter.sortBy.split('-');
      search({
        query,
        options: dataTypes,
        precision_search: filter.precision_search,
        limit: 20,
        number_of_highlighted_fragments: 10,
        search_after: searchAfter,
        date_range: filter.date_range === null ? [] : filter.date_range,
        sort_by: [{ [field]: order }],
      })
        .unwrap()
        .then((newData) => {
          setCombinedData((prevState) => {
            return { ...newData, hits: [...prevState?.hits, ...newData?.hits] };
          });
        });
    }, 300),
    [search],
  );

  useEffect(() => {
    const q = searchParams.get('q');
    const tab = searchParams.get('tab');
    if (q && !isActiveTypesIsLoading) {
      setUsedActivedTypes(tab !== null && tab !== 'all' ? [tab] : activeTypes);
      setCombinedData({ hits: [] });
      setSearchQuery(q);
      setSelectedTab(tab ? tab : selectedTab);
      debouncedSearch(
        q,
        [],
        tab !== null && tab !== 'all' ? [tab] : activeTypes,
        filters,
      );
    }
  }, [isActiveTypesIsLoading, searchParams, debouncedSearch]);

  // Handle typing in the search input
  const handleInputChange = (e) => {
    const value = e.target.value;
    setCombinedData({ hits: [] });
    setSearchQuery(value);
    debouncedSearch(value, [], usedActiveTypes, filters);
  };

  const handleOnSearch = () => {
    setCombinedData({ hits: [] });
    debouncedSearch(searchQuery, [], usedActiveTypes, filters);
  };

  const handleOnFilterChange = (newFilter) => {
    setCombinedData({ hits: [] });
    debouncedSearch(searchQuery, [], usedActiveTypes, newFilter);
  };

  const handleSelect = (url, model_type) => {
    if (model_type === 'files') {
      // open on new tab
      window.open(url, '_blank');
    } else {
      navigate(
        {
          pathname: routes.search,
          search: `?q=${encodeURIComponent(searchQuery)}&tab=${selectedTab}`,
        },
        { replace: true },
      );
      navigate(url);
    }
  };

  const handleLoadMore = (searchAfter) => {
    debouncedSearch(searchQuery, searchAfter, usedActiveTypes, filters);
  };

  const handleOnTabChange = (key) => {
    setCombinedData({ hits: [] });
    if (key === 'all') {
      setUsedActivedTypes(activeTypes);
      debouncedSearch(searchQuery, [], activeTypes, filters);
      setSelectedTab('all');
    } else {
      setUsedActivedTypes([key]);
      debouncedSearch(searchQuery, [], [key], filters);
      setSelectedTab(key);
    }
  };

  const items = [
    {
      key: 'all',
      label: t('Všetko'),
      children: (
        <SearchResultWrapper
          onSelect={handleSelect}
          data={combinedData}
          loadMore={handleLoadMore}
          loading={isLoading || isActiveTypesIsLoading}
        />
      ),
    },
  ];

  activeTypes?.map((type) => {
    items.push({
      key: type,
      label: getSearchTypeName(type),
      children: (
        <SearchResultWrapper
          onSelect={handleSelect}
          data={combinedData}
          loadMore={handleLoadMore}
          loading={isLoading || isActiveTypesIsLoading}
        />
      ),
    });
  });

  return (
    <>
      <Row>
        <Col span={6} xs={{ span: 0 }} sm={{ span: 0 }} md={{ span: 0 }} />

        <Col span={18} xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }}>
          <Search
            placeholder={t('Vyhľadať...')}
            value={searchQuery}
            onChange={handleInputChange}
            onSearch={handleOnSearch}
            loading={isLoading || isActiveTypesIsLoading}
            allowClear
            //enterButton //probably bug in antd design, the button is not aligned
            size="large"
            ref={inputRef}
            style={{ width: '100%', marginBottom: 10 }}
          />
        </Col>
      </Row>
      <Row gutter={[16, 16]}>
        <Col span={6} xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }}>
          <Collapse
            items={[
              {
                key: '1',
                label: t('Pokročilé filtrovanie'),
                children: (
                  <SearchResultFilter onChange={handleOnFilterChange} />
                ),
              },
            ]}
            defaultActiveKey={filters.collapsed}
            onChange={(value) =>
              dispach(saveFilter({ ...filters, collapsed: value }))
            }
          />
        </Col>
        <Col span={18} xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }}>
          {activeTypes && (
            <Tabs
              defaultActiveKey={selectedTab}
              items={items}
              onChange={handleOnTabChange}
            />
          )}
        </Col>
      </Row>
    </>
  );
};

export default SearchPage;
